import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Mail, CheckCircle, Eye, MousePointerClick, AlertTriangle, XCircle,
  TrendingUp, RefreshCw, Filter, ChevronLeft, ChevronRight, Users, Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EVENT_COLORS = {
  delivered: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle },
  open: { bg: 'bg-blue-500/10', text: 'text-blue-400', icon: Eye },
  click: { bg: 'bg-indigo-500/10', text: 'text-indigo-400', icon: MousePointerClick },
  bounce: { bg: 'bg-amber-500/10', text: 'text-amber-400', icon: AlertTriangle },
  dropped: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
  processed: { bg: 'bg-zinc-500/10', text: 'text-zinc-400', icon: Mail },
  failed: { bg: 'bg-red-500/10', text: 'text-red-400', icon: XCircle },
};

const EventBadge = ({ type }) => {
  const cfg = EVENT_COLORS[type] || EVENT_COLORS.processed;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3 h-3" />
      {type}
    </span>
  );
};

const MetricCard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="p-5 rounded-xl bg-white/5 border border-white/10" data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs text-zinc-500 uppercase tracking-wider font-medium">{label}</span>
      <div className={`w-8 h-8 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white/80" />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
  </div>
);

const MiniBar = ({ data, maxVal }) => {
  if (!data || data.length === 0) return null;
  const max = maxVal || Math.max(...data.map(d => d.value), 1);
  return (
    <div className="flex items-end gap-[2px] h-16">
      {data.map((d, i) => (
        <div
          key={i}
          className="flex-1 bg-indigo-500/60 hover:bg-indigo-400/80 rounded-t transition-colors min-w-[3px]"
          style={{ height: `${Math.max((d.value / max) * 100, 4)}%` }}
          title={`${d.label}: ${d.value}`}
        />
      ))}
    </div>
  );
};

const EngagementBar = ({ score }) => {
  const color = score >= 60 ? 'bg-emerald-500' : score >= 30 ? 'bg-amber-500' : 'bg-red-500';
  const label = score >= 60 ? 'High' : score >= 30 ? 'Medium' : 'Low';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${Math.min(score, 100)}%` }} />
      </div>
      <span className={`text-xs font-medium ${score >= 60 ? 'text-emerald-400' : score >= 30 ? 'text-amber-400' : 'text-red-400'}`}>
        {score}% ({label})
      </span>
    </div>
  );
};

const UserEngagementTable = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('engagementScore');
  const limit = 15;

  const fetchEngagement = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/email/analytics/user-engagement?sort_by=${sortBy}&order=desc&page=${page}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('User engagement fetch error:', err);
    }
    setLoading(false);
  }, [token, sortBy, page]);

  useEffect(() => { fetchEngagement(); }, [fetchEngagement]);

  const totalPages = Math.ceil(total / limit);

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-4 h-4 text-indigo-400 animate-spin" />
        <span className="ml-2 text-zinc-400 text-sm">Loading user engagement...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="user-engagement-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-medium text-white">User Engagement Profiles</h3>
          <span className="text-xs text-zinc-500">({total} users tracked)</span>
        </div>
        <Select value={sortBy} onValueChange={(v) => { setSortBy(v); setPage(1); }}>
          <SelectTrigger className="w-[160px] bg-white/5 border-white/10 text-white text-xs h-8" data-testid="engagement-sort-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="engagementScore">Engagement Score</SelectItem>
            <SelectItem value="totalSent">Emails Sent</SelectItem>
            <SelectItem value="totalOpened">Emails Opened</SelectItem>
            <SelectItem value="totalClicked">Emails Clicked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">User</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Sent</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Delivered</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Opened</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Clicked</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Engagement</th>
                <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Last Active</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-zinc-600">
                    No user engagement data yet. Metrics will populate as emails are sent and tracked.
                  </td>
                </tr>
              ) : (
                users.map((u, i) => (
                  <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`engagement-row-${u.email}`}>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm text-white font-medium">{u.userName || 'External'}</p>
                        <p className="text-xs text-zinc-500 font-mono">{u.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{u.totalSent || 0}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{u.totalDelivered || 0}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{u.totalOpened || 0}</td>
                    <td className="px-4 py-3 text-sm text-zinc-300 font-mono">{u.totalClicked || 0}</td>
                    <td className="px-4 py-3">
                      <EngagementBar score={u.engagementScore || 0} />
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                      {u.lastOpenedAt ? new Date(u.lastOpenedAt).toLocaleDateString() : u.lastClickedAt ? new Date(u.lastClickedAt).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
            <span className="text-xs text-zinc-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="border-white/10 text-zinc-400 hover:text-white h-7 px-2">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="border-white/10 text-zinc-400 hover:text-white h-7 px-2">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const EmailAnalytics = () => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [eventFilter, setEventFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [activeView, setActiveView] = useState('overview');
  const limit = 15;

  const fetchData = useCallback(async () => {
    setLoading(true);
    const authHeaders = { Authorization: `Bearer ${token}` };
    try {
      const [sumRes, tlRes, evRes] = await Promise.all([
        fetch(`${API_URL}/api/email/analytics/summary?days=${days}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/email/analytics/timeline?days=${days}`, { headers: authHeaders }),
        fetch(`${API_URL}/api/email/analytics/events?days=${days}&page=${page}&limit=${limit}${eventFilter !== 'all' ? `&event_type=${eventFilter}` : ''}`, { headers: authHeaders }),
      ]);
      const [sumData, tlData, evData] = await Promise.all([sumRes.json(), tlRes.json(), evRes.json()]);
      setSummary(sumData);
      setTimeline(tlData.timeline || []);
      setEvents(evData.events || []);
      setEventsTotal(evData.total || 0);
    } catch (err) {
      console.error('Email analytics fetch error:', err);
    }
    setLoading(false);
  }, [days, eventFilter, page, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(eventsTotal / limit);

  const chartData = timeline.map(d => ({
    label: d.date,
    value: (d.delivered || 0) + (d.open || 0) + (d.click || 0) + (d.bounce || 0) + (d.dropped || 0),
  }));

  const distData = summary ? [
    { label: 'Delivered', value: summary.delivered, color: 'bg-emerald-500' },
    { label: 'Opened', value: summary.opened, color: 'bg-blue-500' },
    { label: 'Clicked', value: summary.clicked, color: 'bg-indigo-500' },
    { label: 'Bounced', value: summary.bounced, color: 'bg-amber-500' },
    { label: 'Dropped', value: summary.dropped, color: 'bg-red-500' },
  ] : [];
  const distTotal = distData.reduce((s, d) => s + d.value, 0) || 1;

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />
        <span className="ml-2 text-zinc-400">Loading email analytics...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-analytics">
      {/* View Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5">
          <button
            onClick={() => setActiveView('overview')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeView === 'overview' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid="view-tab-overview"
          >
            <TrendingUp className="w-3.5 h-3.5" /> Overview
          </button>
          <button
            onClick={() => setActiveView('engagement')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeView === 'engagement' ? 'bg-white/10 text-white' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid="view-tab-engagement"
          >
            <Users className="w-3.5 h-3.5" /> User Engagement
          </button>
        </div>
        <div className="flex items-center gap-3">
          {activeView === 'overview' && (
            <Select value={days} onValueChange={(v) => { setDays(v); setPage(1); }}>
              <SelectTrigger className="w-[140px] bg-white/5 border-white/10 text-white" data-testid="analytics-period-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
                <SelectItem value="365">Last year</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={fetchData} className="border-white/10 text-zinc-400 hover:text-white hover:bg-white/5" data-testid="analytics-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {activeView === 'engagement' ? (
        <UserEngagementTable token={token} />
      ) : (
        <>
          {/* Metric Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <MetricCard label="Total Sent" value={summary.total_sent} icon={Mail} color="bg-indigo-500/20" />
              <MetricCard label="Delivered" value={summary.delivered} sub={`${summary.delivery_rate}%`} icon={CheckCircle} color="bg-emerald-500/20" />
              <MetricCard label="Opened" value={summary.opened} sub={`${summary.open_rate}%`} icon={Eye} color="bg-blue-500/20" />
              <MetricCard label="Clicked" value={summary.clicked} sub={`${summary.click_rate}%`} icon={MousePointerClick} color="bg-indigo-500/20" />
              <MetricCard label="Bounced" value={summary.bounced} sub={`${summary.bounce_rate}%`} icon={AlertTriangle} color="bg-amber-500/20" />
              <MetricCard label="Dropped" value={summary.dropped} icon={XCircle} color="bg-red-500/20" />
            </div>
          )}

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 p-5 rounded-xl bg-white/5 border border-white/10" data-testid="analytics-timeline-chart">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-white flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-indigo-400" />
                  Emails Over Time
                </h3>
                <span className="text-xs text-zinc-500">{timeline.length} days with activity</span>
              </div>
              {chartData.length > 0 ? (
                <MiniBar data={chartData} />
              ) : (
                <p className="text-xs text-zinc-600 text-center py-6">No data for this period</p>
              )}
            </div>

            <div className="p-5 rounded-xl bg-white/5 border border-white/10" data-testid="analytics-distribution">
              <h3 className="text-sm font-medium text-white mb-4">Event Distribution</h3>
              <div className="space-y-3">
                {distData.map((d) => (
                  <div key={d.label} className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full ${d.color}`} />
                    <span className="text-xs text-zinc-400 flex-1">{d.label}</span>
                    <span className="text-xs font-mono text-white">{d.value}</span>
                    <div className="w-16 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${d.color} transition-all`}
                        style={{ width: `${(d.value / distTotal) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Events Table */}
          <div className="rounded-xl bg-white/5 border border-white/10 overflow-hidden" data-testid="analytics-events-table">
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                <Filter className="w-4 h-4 text-zinc-400" />
                Recent Events
                <span className="text-xs text-zinc-500 font-normal">({eventsTotal} total)</span>
              </h3>
              <Select value={eventFilter} onValueChange={(v) => { setEventFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[130px] bg-white/5 border-white/10 text-white text-xs h-8" data-testid="analytics-event-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Events</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="open">Opened</SelectItem>
                  <SelectItem value="click">Clicked</SelectItem>
                  <SelectItem value="bounce">Bounced</SelectItem>
                  <SelectItem value="dropped">Dropped</SelectItem>
                  <SelectItem value="processed">Processed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-white/5">
                    <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Event</th>
                    <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Subject</th>
                    <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Time</th>
                    <th className="px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-zinc-600">
                        No events found for this filter
                      </td>
                    </tr>
                  ) : (
                    events.map((ev, i) => (
                      <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-3"><EventBadge type={ev.event_type} /></td>
                        <td className="px-4 py-3 text-sm text-zinc-300 font-mono truncate max-w-[200px]">{ev.email}</td>
                        <td className="px-4 py-3 text-sm text-zinc-400 truncate max-w-[250px]">{ev.subject || '\u2014'}</td>
                        <td className="px-4 py-3 text-xs text-zinc-500 whitespace-nowrap">
                          {ev.timestamp ? new Date(ev.timestamp).toLocaleString() : '\u2014'}
                        </td>
                        <td className="px-4 py-3 text-xs text-zinc-600 truncate max-w-[200px]">
                          {ev.reason || ev.url || '\u2014'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
                <span className="text-xs text-zinc-500">Page {page} of {totalPages}</span>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="border-white/10 text-zinc-400 hover:text-white h-7 px-2">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="border-white/10 text-zinc-400 hover:text-white h-7 px-2">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
